import JsCookies from 'js-cookie'

import { CookieCategory } from '@tribeplatform/react-sdk/types'

type GetCookiesToRemoveParams = {
  domain: string
  path?: string
}

type Script = {
  name: string
  consentCategory: CookieCategory
  load?: () => void
  unload?: () => void
  /**
   * Returns the cookies to remove when the script is unloaded.
   * @param network - The network object. Could be used to get cookie options (e.g. path, domain, etc)
   */
  getCookiesToRemove?: (
    params: GetCookiesToRemoveParams,
  ) => { name: string; options?: typeof JsCookies.attributes }[]
}

export class ScriptManager {
  private scripts: Script[] = []

  private domain: string

  private domainSubfolder: string | undefined

  private processed: boolean = false

  constructor({
    domain,
    domainSubfolder,
  }: {
    domain: string
    domainSubfolder?: string
  }) {
    this.scripts = []
    this.domain = domain
    this.domainSubfolder = domainSubfolder
  }

  async register(script: Script) {
    this.scripts.push(script)

    /**
     * The scripts are processed when window.BM is ready.
     * In case the scripts are registered after the initial scripts have been processed,
     * we need to process the scripts immediately. Otherwise, until the user changes the cookie consent,
     * the scripts will not be processed.
     */
    if (this.processed) {
      const cookies = this.processScript(script)
      if (cookies.length > 0) {
        await this.removeCookies(cookies)
      }
    }
  }

  isRegistered(name: string) {
    return this.scripts.some(script => script.name === name)
  }

  processScript(script: Script) {
    const isConsentGranted = window.BM.cookieManager.isConsentGranted({
      category: script.consentCategory,
      name: script.name,
    })

    if (isConsentGranted) {
      if (typeof script.load === 'function') {
        script.load()
      }
      return []
    }

    if (typeof script.unload === 'function') {
      script.unload()
    }

    if (typeof script.getCookiesToRemove === 'function') {
      return (
        script.getCookiesToRemove({
          domain: this.domain,
          path: this.domainSubfolder,
        }) ?? []
      )
    }

    return []
  }

  async processScripts() {
    this.processed = true

    const cookiesToRemove = this.scripts.flatMap(script =>
      this.processScript(script),
    )
    if (cookiesToRemove.length > 0) {
      await this.removeCookies(cookiesToRemove)
    }
  }

  async removeCookies(
    cookies: {
      name: string
      options?: typeof JsCookies.attributes
    }[],
  ) {
    try {
      return fetch('/api/remove-cookies', {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify({ cookies }),
      })
    } catch (error) {
      return error
    }
  }
}
